import { useModuleAccess } from "@/hooks/useModuleAccess";
import React, { useState, useEffect, useRef } from "react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { getAiFabEnabled, setAiFabEnabled } from "@/hooks/useAiFabEnabled";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sun, Moon, Monitor, Globe, Bell, Clock, Lock, Bot,
  Plus, Trash2, CheckCircle2, Circle, Calendar,
  AlertCircle, FileText, Users, DollarSign, CalendarDays,
  ChevronRight, Pencil, Check, X, LayoutDashboard,
  UserCheck, ClipboardList, ShieldAlert, GripVertical,
  Link2, CalendarCheck2,
  BarChart3, Briefcase, UserPlus, BookOpen, Gift, Settings2,
  ShieldCheck, Download, Smartphone, QrCode,
  Stethoscope, GraduationCap, Laptop, ShieldQuestion, Compass,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  apiGetNotificationPrefs, apiUpdateNotificationPrefs,
  apiGetReminders, apiCreateReminder, apiUpdateReminder, apiDeleteReminder,
  apiChangePassword, apiGetUiPrefs, apiSetUiPref, getTenantSlug,
  apiGetCalendarStatus, apiDisconnectCalendar, getCalendarOAuthUrl,
  apiGet2FAStatus, apiSetup2FA, apiEnable2FA, apiDisable2FA, apiExportMyData,
  type NotificationPrefs, type Reminder,
} from "@/lib/api";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

// ── Notification types config ─────────────────────────────────────────────────
const NOTIF_GROUP_DEFS = [
  {
    groupKey: "leave",
    icon: CalendarDays,
    color: "text-blue-600",
    types: ["leave_submitted", "leave_approved", "leave_rejected", "leave_replacement", "leave_cancelled"],
  },
  {
    groupKey: "documents",
    icon: FileText,
    color: "text-purple-600",
    types: ["document_uploaded", "document_completed", "document_rejected", "document_request"],
  },
  {
    groupKey: "eventsPayroll",
    icon: DollarSign,
    color: "text-green-600",
    types: ["event_new", "salary_processed"],
  },
  {
    groupKey: "recruitment",
    icon: Briefcase,
    color: "text-cyan-600",
    types: ["recruitment_application_received"],
  },
];

const REMINDER_CATEGORY_COLORS: Record<string, string> = {
  general: "bg-gray-100 text-gray-700",
  document: "bg-orange-100 text-orange-700",
  leave: "bg-blue-100 text-blue-700",
  meeting: "bg-purple-100 text-purple-700",
  task: "bg-green-100 text-green-700",
  personal: "bg-pink-100 text-pink-700",
};

const REMINDER_CATEGORY_VALUES = ["general", "document", "leave", "meeting", "task", "personal"];
const REPEAT_OPTION_VALUES = ["none", "daily", "weekly", "monthly", "yearly"];

function categoryStyle(cat: string) {
  return REMINDER_CATEGORY_COLORS[cat] ?? "bg-gray-100 text-gray-700";
}

// ── Appearance Tab ─────────────────────────────────────────────────────────────
function AppearanceTab() {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const [saraFab, setSaraFab] = useState<boolean>(getAiFabEnabled());

  const options = [
    { value: "light", label: t("settings.appearance.light"), icon: Sun, desc: t("settings.appearance.lightDesc") },
    { value: "dark", label: t("settings.appearance.dark"), icon: Moon, desc: t("settings.appearance.darkDesc") },
    { value: "system", label: t("settings.appearance.system"), icon: Monitor, desc: t("settings.appearance.systemDesc") },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">{t("settings.appearance.title")}</h2>
        <p className="text-sm text-muted-foreground mt-0.5">{t("settings.appearance.subtitle")}</p>
      </div>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("settings.appearance.themeCard")}</CardTitle>
          <CardDescription>{t("settings.appearance.themeCardDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            {options.map(({ value, label, icon: Icon, desc }) => {
              const active = (theme ?? "system") === value;
              return (
                <button
                  key={value}
                  onClick={() => setTheme(value)}
                  className={`relative flex flex-col items-center gap-2 rounded-xl border-2 p-4 text-center transition-all ${
                    active
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/40"
                  }`}
                >
                  {active && (
                    <span className="absolute top-2 right-2 h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                      <Check className="h-3 w-3 text-primary-foreground" />
                    </span>
                  )}
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${active ? "bg-primary/10" : "bg-secondary"}`}>
                    <Icon className={`h-5 w-5 ${active ? "text-primary" : "text-muted-foreground"}`} />
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${active ? "text-primary" : ""}`}>{label}</p>
                    <p className="text-[11px] text-muted-foreground leading-tight">{desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("settings.assistant.title", { defaultValue: "AI assistant" })}</CardTitle>
          <CardDescription>{t("settings.assistant.desc", { defaultValue: "Control the floating “Ask Sara” button." })}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Bot className="h-4 w-4 text-primary" />
              </div>
              <div>
                <Label htmlFor="sara-fab" className="text-sm font-medium">
                  {t("settings.assistant.fabLabel", { defaultValue: "Show the “Ask Sara” button" })}
                </Label>
                <p className="text-[11px] text-muted-foreground leading-tight">
                  {t("settings.assistant.fabHint", { defaultValue: "The floating button in the bottom-right corner on every page." })}
                </p>
              </div>
            </div>
            <Switch
              id="sara-fab"
              checked={saraFab}
              onCheckedChange={(v) => { setSaraFab(v); setAiFabEnabled(v); }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Language Tab ───────────────────────────────────────────────────────────────
function LanguageTab() {
  const { t, i18n } = useTranslation();

  const languages = [
    { code: "en", label: "English", flag: "🇬🇧", desc: "English (UK)" },
    { code: "mk", label: "Macedonian", flag: "🇲🇰", desc: "Македонски" },
    { code: "sr", label: "Serbian", flag: "🇷🇸", desc: "Српски" },
  ];

  function changeLang(code: string) {
    i18n.changeLanguage(code);
    localStorage.setItem("besthr_lang", code);
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">{t("settings.language.title")}</h2>
        <p className="text-sm text-muted-foreground mt-0.5">{t("settings.language.subtitle")}</p>
      </div>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("settings.language.cardTitle")}</CardTitle>
          <CardDescription>{t("settings.language.cardDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3">
            {languages.map(({ code, label, flag, desc }) => {
              const active = i18n.language === code;
              return (
                <button
                  key={code}
                  onClick={() => changeLang(code)}
                  className={`flex items-center gap-4 rounded-xl border-2 p-4 text-left transition-all ${
                    active
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/40"
                  }`}
                >
                  <span className="text-3xl">{flag}</span>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${active ? "text-primary" : ""}`}>{label}</p>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                  {active && (
                    <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                      <Check className="h-3 w-3 text-primary-foreground" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Notifications Tab ──────────────────────────────────────────────────────────
function NotificationsTab() {
  const { canAccess } = useModuleAccess();
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    apiGetNotificationPrefs()
      .then(setPrefs)
      .catch(() => {});
  }, []);

  function toggle(type: string, channel: "inApp" | "email") {
    if (!prefs) return;
    setPrefs({
      ...prefs,
      [type]: { ...prefs[type], [channel]: !prefs[type][channel] },
    });
  }

  async function handleSave() {
    if (!prefs) return;
    setSaving(true);
    try {
      const payload = Object.entries(prefs).map(([type, p]) => ({
        type,
        inApp: p.inApp,
        email: p.email,
      }));
      await apiUpdateNotificationPrefs(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* ignore */ }
    finally { setSaving(false); }
  }

  if (!prefs) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">{t("settings.notifications.title")}</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{t("settings.notifications.subtitle")}</p>
        </div>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {saved ? <><Check className="h-4 w-4 mr-1.5" />{t("settings.notifications.saved")}</> : saving ? t("settings.notifications.saving") : t("settings.notifications.saveChanges")}
        </Button>
      </div>

      {/* Channel legend */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              <span>{t("settings.notifications.legendInApp")}</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              <span>{t("settings.notifications.legendEmail")}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {NOTIF_GROUP_DEFS.map(group => ({ ...group, types: group.types.filter(type => type !== "salary_processed" || canAccess("salary")) })).filter(group => group.types.length > 0).map(({ groupKey, icon: Icon, color, types }) => (
        <Card key={groupKey}>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Icon className={`h-4 w-4 ${color}`} />
              <CardTitle className="text-sm font-semibold">{t(`settings.notifications.groups.${groupKey === "eventsPayroll" && !types.includes("salary_processed") ? "eventsOnly" : groupKey}`)}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="divide-y">
              {/* Header row */}
              <div className="grid grid-cols-[1fr_80px_80px] gap-2 pb-2 text-xs font-medium text-muted-foreground">
                <span>{t("settings.notifications.columnNotification")}</span>
                <span className="text-center flex items-center justify-center gap-1"><Bell className="h-3 w-3" />{t("settings.notifications.columnInApp")}</span>
                <span className="text-center flex items-center justify-center gap-1"><Globe className="h-3 w-3" />{t("settings.notifications.columnEmail")}</span>
              </div>
              {types.map((key) => (
                <div key={key} className="grid grid-cols-[1fr_80px_80px] gap-2 items-center py-2.5">
                  <span className="text-sm">{t(`settings.notifications.types.${key}`)}</span>
                  <div className="flex justify-center">
                    <Switch
                      checked={prefs[key]?.inApp ?? true}
                      onCheckedChange={() => toggle(key, "inApp")}
                    />
                  </div>
                  <div className="flex justify-center">
                    <Switch
                      checked={prefs[key]?.email ?? false}
                      onCheckedChange={() => toggle(key, "email")}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Reminders Tab ──────────────────────────────────────────────────────────────
function RemindersTab() {
  const { t } = useTranslation();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [category, setCategory] = useState("general");
  const [repeat, setRepeat] = useState("none");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGetReminders()
      .then(setReminders)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function openCreate() {
    setEditingId(null);
    setTitle("");
    setDescription("");
    setDueDate("");
    setCategory("general");
    setRepeat("none");
    setShowForm(true);
  }

  function openEdit(r: Reminder) {
    setEditingId(r.id);
    setTitle(r.title);
    setDescription(r.description ?? "");
    setDueDate(r.dueDate ? r.dueDate.split("T")[0] : "");
    setCategory(r.category);
    setRepeat(r.repeat);
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setEditingId(null);
  }

  async function handleSubmit() {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || undefined,
        dueDate: dueDate || undefined,
        category,
        repeat,
      };
      if (editingId !== null) {
        const updated = await apiUpdateReminder(editingId, payload);
        setReminders((prev) => prev.map((r) => (r.id === editingId ? updated : r)));
      } else {
        const created = await apiCreateReminder(payload);
        setReminders((prev) => [created, ...prev]);
      }
      cancelForm();
    } catch { /* ignore */ }
    finally { setSaving(false); }
  }

  async function toggleDone(r: Reminder) {
    try {
      const updated = await apiUpdateReminder(r.id, { isDone: !r.isDone });
      setReminders((prev) => prev.map((x) => (x.id === r.id ? updated : x)));
    } catch { /* ignore */ }
  }

  async function handleDelete(id: number) {
    try {
      await apiDeleteReminder(id);
      setReminders((prev) => prev.filter((r) => r.id !== id));
    } catch { /* ignore */ }
  }

  const active = reminders.filter((r) => !r.isDone);
  const done = reminders.filter((r) => r.isDone);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">{t("settings.reminders.title")}</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t("settings.reminders.subtitle")}
          </p>
        </div>
        {!showForm && (
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1.5" />{t("settings.reminders.newReminder")}
          </Button>
        )}
      </div>

      {/* Create / Edit form */}
      {showForm && (
        <Card className="border-primary/40 shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">{editingId !== null ? t("settings.reminders.editReminder") : t("settings.reminders.newReminder")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-xs">{t("settings.reminders.titleField")}</Label>
              <Input
                placeholder={t("settings.reminders.titlePlaceholder")}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs">{t("settings.reminders.descriptionField")}</Label>
              <Textarea
                placeholder={t("settings.reminders.descriptionPlaceholder")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-[70px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block text-xs">{t("settings.reminders.dueDateField")}</Label>
                <DatePicker value={dueDate} onChange={setDueDate} className="w-full" />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs">{t("settings.reminders.categoryField")}</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REMINDER_CATEGORY_VALUES.map((v) => (
                      <SelectItem key={v} value={v}>{t(`settings.reminders.categories.${v}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs">{t("settings.reminders.repeatField")}</Label>
              <Select value={repeat} onValueChange={setRepeat}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REPEAT_OPTION_VALUES.map((v) => (
                    <SelectItem key={v} value={v}>{t(`settings.reminders.repeat.${v}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={handleSubmit} disabled={!title.trim() || saving}>
                {saving ? t("settings.reminders.saving") : editingId !== null ? t("settings.reminders.update") : t("settings.reminders.create")}
              </Button>
              <Button size="sm" variant="ghost" onClick={cancelForm}>{t("settings.reminders.cancel")}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      ) : reminders.length === 0 && !showForm ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Clock className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium text-muted-foreground">{t("settings.reminders.emptyTitle")}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t("settings.reminders.emptyDesc")}
            </p>
            <Button size="sm" className="mt-4" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-1.5" />{t("settings.reminders.createFirst")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {active.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("settings.reminders.activeSection")} ({active.length})</p>
              {active.map((r) => (
                <ReminderRow
                  key={r.id}
                  reminder={r}
                  onToggle={() => toggleDone(r)}
                  onEdit={() => openEdit(r)}
                  onDelete={() => handleDelete(r.id)}
                />
              ))}
            </div>
          )}
          {done.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("settings.reminders.completedSection")} ({done.length})</p>
              {done.map((r) => (
                <ReminderRow
                  key={r.id}
                  reminder={r}
                  onToggle={() => toggleDone(r)}
                  onEdit={() => openEdit(r)}
                  onDelete={() => handleDelete(r.id)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ReminderRow({
  reminder: r,
  onToggle,
  onEdit,
  onDelete,
}: {
  reminder: Reminder;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const isOverdue = r.dueDate && !r.isDone && new Date(r.dueDate) < new Date();

  return (
    <Card className={r.isDone ? "opacity-60" : isOverdue ? "border-red-200 bg-red-50/30 dark:bg-red-950/10" : ""}>
      <CardContent className="p-3 flex items-start gap-3">
        <button
          onClick={onToggle}
          className="mt-0.5 shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-all hover:scale-110"
          style={{ borderColor: r.isDone ? "#22c55e" : "#d1d5db", background: r.isDone ? "#22c55e" : "transparent" }}
        >
          {r.isDone && <Check className="h-3 w-3 text-white" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <span className={`text-sm font-medium ${r.isDone ? "line-through text-muted-foreground" : ""}`}>
              {r.title}
            </span>
            <Badge className={`text-[10px] px-1.5 py-0 ${categoryStyle(r.category)}`}>
              {t(`settings.reminders.categories.${r.category}`, { defaultValue: r.category })}
            </Badge>
            {r.repeat !== "none" && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                {t(`settings.reminders.repeat.${r.repeat}`, { defaultValue: r.repeat })}
              </Badge>
            )}
          </div>
          {r.description && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{r.description}</p>
          )}
          {r.dueDate && (
            <div className={`flex items-center gap-1 mt-1 text-xs ${isOverdue ? "text-red-600 font-medium" : "text-muted-foreground"}`}>
              <Calendar className="h-3 w-3" />
              {new Date(r.dueDate).toLocaleDateString()}
              {isOverdue && <span className="ml-1">— {t("settings.reminders.overdue")}</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Security Tab ───────────────────────────────────────────────────────────────
function SecurityTab() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // ── Password change ──────────────────────────────────────────────────────
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // ── 2FA state ────────────────────────────────────────────────────────────
  const [twoFaModal, setTwoFaModal] = useState<"setup" | "disable" | null>(null);
  const [twoFaSecret, setTwoFaSecret] = useState("");
  const [twoFaQrUri, setTwoFaQrUri] = useState("");
  const [twoFaCode, setTwoFaCode] = useState("");
  const [twoFaPassword, setTwoFaPassword] = useState("");
  const [twoFaError, setTwoFaError] = useState("");
  const [twoFaSaving, setTwoFaSaving] = useState(false);

  const { data: twoFaStatus } = useQuery({
    queryKey: ["2fa-status"],
    queryFn: apiGet2FAStatus,
  });

  async function handleSetup2FA() {
    setTwoFaError("");
    setTwoFaSaving(true);
    try {
      const { secret, qrUri } = await apiSetup2FA();
      setTwoFaSecret(secret);
      setTwoFaQrUri(qrUri);
      setTwoFaCode("");
      setTwoFaModal("setup");
    } catch (e: any) {
      toast.error(e?.message ?? t("settings.security.twoFaSetupError"));
    } finally {
      setTwoFaSaving(false);
    }
  }

  async function handleEnable2FA(e: React.FormEvent) {
    e.preventDefault();
    if (twoFaCode.length !== 6) return;
    setTwoFaError("");
    setTwoFaSaving(true);
    try {
      await apiEnable2FA(twoFaSecret, twoFaCode);
      queryClient.invalidateQueries({ queryKey: ["2fa-status"] });
      setTwoFaModal(null);
      toast.success(t("settings.security.twoFaEnabled"));
    } catch (e: any) {
      setTwoFaError(e?.message ?? t("settings.security.invalidCode"));
    } finally {
      setTwoFaSaving(false);
    }
  }

  async function handleDisable2FA(e: React.FormEvent) {
    e.preventDefault();
    if (!twoFaPassword) return;
    setTwoFaError("");
    setTwoFaSaving(true);
    try {
      await apiDisable2FA(twoFaPassword);
      queryClient.invalidateQueries({ queryKey: ["2fa-status"] });
      setTwoFaModal(null);
      setTwoFaPassword("");
      toast.success(t("settings.security.twoFaDisabled"));
    } catch (e: any) {
      setTwoFaError(e?.message ?? t("settings.security.incorrectPassword"));
    } finally {
      setTwoFaSaving(false);
    }
  }

  // ── GDPR export state ────────────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);

  async function handleExportData() {
    setExporting(true);
    try {
      await apiExportMyData();
      toast.success(t("settings.security.exportDownloaded"));
    } catch {
      toast.error(t("settings.security.exportError"));
    } finally {
      setExporting(false);
    }
  }

  function validate() {
    if (!current) return t("settings.security.currentPasswordRequired");
    if (next.length < 8) return t("settings.security.newPasswordMin");
    if (next !== confirm) return t("settings.security.passwordMismatch");
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    setSaving(true);
    try {
      await apiChangePassword({ currentPassword: current, newPassword: next });
      setSuccess(true);
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e: any) {
      setError(e?.message ?? t("settings.security.changePasswordError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">{t("settings.security.title")}</h2>
        <p className="text-sm text-muted-foreground mt-0.5">{t("settings.security.subtitle")}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="h-4 w-4" />{t("settings.security.changePassword")}
          </CardTitle>
          <CardDescription>
            {t("settings.security.passwordMinLength")}{" "}
            {user?.email && <span className="text-foreground font-medium">{user.email}</span>}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4 max-w-sm">
            <div>
              <Label className="mb-1.5 block">{t("settings.security.currentPassword")}</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={current}
                onChange={(e) => { setCurrent(e.target.value); setSuccess(false); setError(""); }}
              />
            </div>
            <div>
              <Label className="mb-1.5 block">{t("settings.security.newPassword")}</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={next}
                onChange={(e) => { setNext(e.target.value); setSuccess(false); setError(""); }}
              />
              <p className="text-xs text-muted-foreground mt-1">{t("settings.security.newPasswordHint")}</p>
            </div>
            <div>
              <Label className="mb-1.5 block">{t("settings.security.confirmPassword")}</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={confirm}
                onChange={(e) => { setConfirm(e.target.value); setSuccess(false); setError(""); }}
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}
            {success && (
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                {t("settings.security.passwordSuccess")}
              </div>
            )}

            <Button type="submit" disabled={saving}>
              {saving ? t("settings.security.changing") : t("settings.security.changePasswordBtn")}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* ── Two-Factor Authentication Card ─────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />{t("settings.security.twoFaTitle")}
          </CardTitle>
          <CardDescription>
            {t("settings.security.twoFaDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {twoFaStatus?.enabled ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-green-700">
                <CheckCircle2 className="h-4 w-4" />
                <span>{t("settings.security.twoFaEnabled")} <strong>{t("settings.security.twoFaEnabledBold")}</strong></span>
              </div>
              <Button variant="outline" size="sm" onClick={() => { setTwoFaModal("disable"); setTwoFaPassword(""); setTwoFaError(""); }}>
                {t("settings.security.twoFaDisableBtn")}
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{t("settings.security.twoFaNotEnabled")}</p>
              <Button variant="outline" size="sm" onClick={handleSetup2FA} disabled={twoFaSaving}>
                <Smartphone className="h-4 w-4 mr-1.5" />
                {twoFaSaving ? t("settings.security.twoFaLoading") : t("settings.security.twoFaSetupBtn")}
              </Button>
            </div>
          )}

          {/* Setup modal */}
          {twoFaModal === "setup" && (
            <div className="mt-4 border border-border rounded-xl p-5 bg-muted/30 space-y-4">
              <div className="flex items-start gap-3">
                <QrCode className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium">{t("settings.security.twoFaScanPrompt")}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t("settings.security.twoFaSecretHint")} <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">{twoFaSecret}</code></p>
                </div>
              </div>
              <div className="flex justify-center">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(twoFaQrUri)}`}
                  alt="QR Code"
                  className="rounded-lg border border-border"
                  width={180}
                  height={180}
                />
              </div>
              <form onSubmit={handleEnable2FA} className="space-y-3">
                <p className="text-sm text-center text-muted-foreground">{t("settings.security.twoFaEnterCode")}</p>
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={twoFaCode} onChange={setTwoFaCode} autoFocus>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                {twoFaError && (
                  <p className="text-xs text-destructive text-center">{twoFaError}</p>
                )}
                <div className="flex gap-2 justify-center">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setTwoFaModal(null)}>{t("settings.reminders.cancel")}</Button>
                  <Button type="submit" size="sm" disabled={twoFaSaving || twoFaCode.length !== 6}>
                    {twoFaSaving ? t("settings.security.twoFaEnabling") : t("settings.security.twoFaEnable")}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Disable modal */}
          {twoFaModal === "disable" && (
            <div className="mt-4 border border-border rounded-xl p-5 bg-muted/30 space-y-4">
              <p className="text-sm">{t("settings.security.twoFaDisableConfirm")}</p>
              <form onSubmit={handleDisable2FA} className="space-y-3">
                <Input
                  type="password"
                  placeholder={t("settings.security.twoFaPasswordPlaceholder")}
                  value={twoFaPassword}
                  onChange={(e) => { setTwoFaPassword(e.target.value); setTwoFaError(""); }}
                  autoFocus
                />
                {twoFaError && (
                  <p className="text-xs text-destructive">{twoFaError}</p>
                )}
                <div className="flex gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setTwoFaModal(null)}>{t("settings.reminders.cancel")}</Button>
                  <Button type="submit" size="sm" variant="destructive" disabled={twoFaSaving || !twoFaPassword}>
                    {twoFaSaving ? t("settings.security.twoFaDisabling") : t("settings.security.twoFaDisable")}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── GDPR Data Export Card ────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Download className="h-4 w-4" />{t("settings.security.gdprTitle")}
          </CardTitle>
          <CardDescription>
            {t("settings.security.gdprDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t("settings.security.gdprBody")}
          </p>
          <Button variant="outline" onClick={handleExportData} disabled={exporting}>
            <Download className="h-4 w-4 mr-2" />
            {exporting ? t("settings.security.gdprPreparing") : t("settings.security.gdprDownload")}
          </Button>
        </CardContent>
      </Card>

    </div>
  );
}

// ── Dashboard Widgets Tab ──────────────────────────────────────────────────────

type WidgetDef = { id: string; label: string; desc: string; icon: React.ElementType };

function applyWidgetOrder(widgets: WidgetDef[], savedJson: string | undefined): WidgetDef[] {
  try {
    const saved: string[] = JSON.parse(savedJson ?? "[]");
    if (!Array.isArray(saved) || saved.length === 0) return widgets;
    const byId = Object.fromEntries(widgets.map((w) => [w.id, w]));
    const ordered = saved.filter((id) => byId[id]).map((id) => byId[id]);
    // Append any widgets not in the saved list (newly added widgets)
    widgets.forEach((w) => { if (!saved.includes(w.id)) ordered.push(w); });
    return ordered;
  } catch {
    return widgets;
  }
}

function SortableWidgetRow({
  w, isEnabled, onToggle, disabled,
}: { w: WidgetDef; isEnabled: boolean; onToggle: (v: boolean) => void; disabled: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: w.id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="flex items-center justify-between p-3 rounded-lg border bg-card"
    >
      <div className="flex items-center gap-2">
        <button
          {...attributes} {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground p-0.5 touch-none"
          tabIndex={-1}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center shrink-0">
          <w.icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div>
          <p className="text-sm font-medium">{w.label}</p>
          <p className="text-xs text-muted-foreground">{w.desc}</p>
        </div>
      </div>
      <Switch checked={isEnabled} onCheckedChange={onToggle} disabled={disabled} />
    </div>
  );
}

function DashboardTab() {
  const { canAccess } = useModuleAccess();
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mainRole = user?.role.mainRole ?? "user";
  const isAdminOrHr = mainRole === "admin" || mainRole === "hr";
  const isLineManager = !!(user?.isLineManager || user?.role.subRole === "line_manager");
  const showApprovals = mainRole === "hr" || isLineManager;

  const { data: prefs = {}, isFetched } = useQuery({
    queryKey: ["ui-prefs", getTenantSlug()],
    queryFn: apiGetUiPrefs,
    staleTime: Infinity,
  });

  const mutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => apiSetUiPref(key, value),
    onSuccess: (_, { key, value }) => {
      queryClient.setQueryData<Record<string, string>>(
        ["ui-prefs", getTenantSlug()],
        (old) => ({ ...old, [key]: value }),
      );
    },
  });

  const isEnabled = (id: string) => {
    const k = `widget_${id}_visible`;
    return !(k in prefs) || prefs[k] === "true";
  };
  const toggle = (id: string, checked: boolean) =>
    mutation.mutate({ key: `widget_${id}_visible`, value: String(checked) });

  const w = (id: string, icon: React.ElementType): WidgetDef => ({
    id,
    label: t(`settings.dashboard.widgets.${id}`),
    desc: t(`settings.dashboard.widgets.${id}Desc`),
    icon,
  });

  const widgetAvailable = (id: string) => {
    const owner: Record<string, string> = { surveys: "mySurveys", open_cases: "hrOpenCases", hr_surveys: "surveys", hr_payroll_tasks: "payroll", hr_medical: "medicalCheckups", hr_certifications: "certifications", hr_trainings: "certifications", hr_recruitment: "recruitment", hr_onboarding: "onboarding", hr_equipment: "equipmentManagement" };
    return !owner[id] || canAccess(owner[id]);
  };
  const canonicalMain: WidgetDef[] = [
    w("inbox", Bell),
    w("calendar", CalendarDays),
    w("celebrations", Users),
    ...(showApprovals ? [w("pending_approvals", UserCheck)] : []),
  ];

  const canonicalSidebar: WidgetDef[] = [
    w("profile_card", Users),
    w("time_off", Calendar),
    w("team", Users),
    w("documents", FileText),
    w("my_tasks", ClipboardList),
    w("surveys", FileText),
    w("quick_links", ChevronRight),
    ...(isAdminOrHr  ? [w("open_cases", ShieldAlert)] : []),
    ...(!isAdminOrHr ? [w("raise_concern", AlertCircle)] : []),
  ];

  // Local order state — initialised from prefs once they load
  const [mainWidgets,    setMainWidgets]    = useState<WidgetDef[]>(canonicalMain);
  const [sidebarWidgets, setSidebarWidgets] = useState<WidgetDef[]>(canonicalSidebar);
  const orderInit = useRef(false);

  useEffect(() => {
    if (!isFetched || orderInit.current) return;
    orderInit.current = true;
    setMainWidgets(applyWidgetOrder(canonicalMain, prefs["widget_order_main"]));
    setSidebarWidgets(applyWidgetOrder(canonicalSidebar, prefs["widget_order_sidebar"]));
  }, [isFetched]); // eslint-disable-line react-hooks/exhaustive-deps

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (
    event: DragEndEvent,
    items: WidgetDef[],
    setItems: (v: WidgetDef[]) => void,
    orderKey: string,
  ) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((w) => w.id === active.id);
    const newIdx = items.findIndex((w) => w.id === over.id);
    const next = arrayMove(items, oldIdx, newIdx);
    setItems(next);
    mutation.mutate({ key: orderKey, value: JSON.stringify(next.map((w) => w.id)) });
  };

  const hrWidgets: WidgetDef[] = [
    w("hr_stat_strip", ClipboardList),
    w("hr_recruitment", Briefcase),
    w("hr_leave_requests", Clock),
    w("hr_onboarding", UserPlus),
    w("hr_documents", FileText),
    w("hr_surveys", BookOpen),
    w("hr_headcount", Users),
    w("hr_payroll_tasks", DollarSign),
    w("hr_celebrations", Gift),
    w("hr_medical", Stethoscope),
    w("hr_certifications", ShieldCheck),
    w("hr_trainings", GraduationCap),
    w("hr_equipment", Laptop),
    w("hr_workflow_owners", ShieldQuestion),
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">{t("settings.dashboard.title")}</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          {t("settings.dashboard.subtitle")}
        </p>
      </div>

      <Card>
        <CardContent className="flex items-center justify-between gap-4 py-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Compass className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold">{t("settings.dashboard.tour.title", "Platform tour")}</p>
              <p className="text-sm text-muted-foreground">
                {t("settings.dashboard.tour.subtitle", "Take a guided walkthrough of the dashboard and menus.")}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              // Flag survives the HR "/" → "/hr-dashboard" redirect; the
              // ProductTour controller picks it up on the dashboard.
              try { sessionStorage.setItem("besthr_start_tour", "1"); } catch {}
              navigate("/");
            }}
            className="shrink-0"
          >
            {t("settings.dashboard.tour.button", "Take a tour")}
          </Button>
        </CardContent>
      </Card>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">{t("settings.dashboard.mainContent")}</p>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(e) => handleDragEnd(e, mainWidgets, setMainWidgets, "widget_order_main")}
        >
          <SortableContext items={mainWidgets.filter(w => widgetAvailable(w.id)).map((w) => w.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {mainWidgets.filter(w => widgetAvailable(w.id)).map((w) => (
                <SortableWidgetRow
                  key={w.id} w={w}
                  isEnabled={isEnabled(w.id)}
                  onToggle={(checked) => toggle(w.id, checked)}
                  disabled={mutation.isPending}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">{t("settings.dashboard.sidebar")}</p>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(e) => handleDragEnd(e, sidebarWidgets, setSidebarWidgets, "widget_order_sidebar")}
        >
          <SortableContext items={sidebarWidgets.filter(w => widgetAvailable(w.id)).map((w) => w.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {sidebarWidgets.filter(w => widgetAvailable(w.id)).map((w) => (
                <SortableWidgetRow
                  key={w.id} w={w}
                  isEnabled={isEnabled(w.id)}
                  onToggle={(checked) => toggle(w.id, checked)}
                  disabled={mutation.isPending}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* HR Dashboard widgets — only for admin/HR */}
      {isAdminOrHr && (
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-lg font-semibold">{t("settings.dashboard.hrTitle")}</h2>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5 mb-3">
            {t("settings.dashboard.hrSubtitle")}
          </p>
          <div className="space-y-2">
            {hrWidgets.filter(w => widgetAvailable(w.id)).map((w) => (
              <div
                key={w.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-card px-4 py-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <w.icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{w.label}</p>
                    <p className="text-xs text-muted-foreground truncate">{w.desc}</p>
                  </div>
                </div>
                <Switch
                  checked={isEnabled(w.id)}
                  onCheckedChange={(checked) => toggle(w.id, checked)}
                  disabled={mutation.isPending}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Calendar Integration Tab ──────────────────────────────────────────────────
function CalendarTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: calendarStatus, isLoading } = useQuery({
    queryKey: ["calendar-status"],
    queryFn: apiGetCalendarStatus,
  });
  const disconnect = useMutation({
    mutationFn: apiDisconnectCalendar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar-status"] });
      toast.success(t("settings.calendar.disconnected"));
    },
    onError: () => toast.error(t("settings.calendar.disconnectError")),
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">{t("settings.calendar.title")}</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          {t("settings.calendar.subtitle")}
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarCheck2 className="h-4 w-4 text-violet-600" />
            {t("settings.calendar.cardTitle")}
          </CardTitle>
          <CardDescription>
            {t("settings.calendar.cardDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full rounded-lg" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ) : calendarStatus?.connected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 rounded-lg border bg-green-50 dark:bg-green-950/20 px-4 py-3">
                <Link2 className="h-4 w-4 text-green-600 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-700 dark:text-green-400 capitalize">
                    {calendarStatus.provider} {t("settings.calendar.connected")}
                  </p>
                  {calendarStatus.connectedAt && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t("settings.calendar.connectedAt")} {new Date(calendarStatus.connectedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => disconnect.mutate()}
                  disabled={disconnect.isPending}
                >
                  {t("settings.calendar.disconnect")}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("settings.calendar.switchHint")}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{t("settings.calendar.chooseProvider")}</p>
              <div className="flex gap-3">
                <a
                  href={getCalendarOAuthUrl("microsoft", "/settings?tab=calendar")}
                  className="flex-1 flex items-center justify-center gap-2 rounded-md border border-border bg-card px-4 py-2.5 text-sm font-medium hover:bg-accent transition-colors"
                >
                  <svg className="h-4 w-4" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="10" height="10" fill="#F25022"/>
                    <rect x="11" width="10" height="10" fill="#7FBA00"/>
                    <rect y="11" width="10" height="10" fill="#00A4EF"/>
                    <rect x="11" y="11" width="10" height="10" fill="#FFB900"/>
                  </svg>
                  {t("settings.calendar.microsoft")}
                </a>
                <a
                  href={getCalendarOAuthUrl("google", "/settings?tab=calendar")}
                  className="flex-1 flex items-center justify-center gap-2 rounded-md border border-border bg-card px-4 py-2.5 text-sm font-medium hover:bg-accent transition-colors"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  {t("settings.calendar.google")}
                </a>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main Settings page ─────────────────────────────────────────────────────────
export default function Settings() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("dashboard");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (tab) setActiveTab(tab);
    if (params.get("calendarConnected") === "1") {
      toast.success(t("settings.calendar.connectedSuccess"));
      const url = new URL(window.location.href);
      url.searchParams.delete("calendarConnected");
      url.searchParams.delete("provider");
      window.history.replaceState({}, "", url.toString());
    }
    if (params.get("calendarError")) {
      toast.error(t("settings.calendar.connectError"));
      const url = new URL(window.location.href);
      url.searchParams.delete("calendarError");
      window.history.replaceState({}, "", url.toString());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        <div>
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-slate-500/10 flex items-center justify-center shrink-0">
              <Settings2 className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t("nav.settings")}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t("settings.pageDescription")}
              </p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 flex-wrap">
            <TabsTrigger value="dashboard" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <LayoutDashboard className="h-3.5 w-3.5" />{t("settings.tabs.dashboard")}
            </TabsTrigger>
            <TabsTrigger value="appearance" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Sun className="h-3.5 w-3.5" />{t("settings.tabs.appearance")}
            </TabsTrigger>
            <TabsTrigger value="language" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Globe className="h-3.5 w-3.5" />{t("settings.tabs.language")}
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Bell className="h-3.5 w-3.5" />{t("settings.tabs.notifications")}
            </TabsTrigger>
            <TabsTrigger value="reminders" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Clock className="h-3.5 w-3.5" />{t("settings.tabs.reminders")}
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Lock className="h-3.5 w-3.5" />{t("settings.tabs.security")}
            </TabsTrigger>
            <TabsTrigger value="calendar" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <CalendarDays className="h-3.5 w-3.5" />{t("settings.tabs.calendar")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-6">
            <DashboardTab />
          </TabsContent>
          <TabsContent value="appearance" className="mt-6">
            <AppearanceTab />
          </TabsContent>
          <TabsContent value="language" className="mt-6">
            <LanguageTab />
          </TabsContent>
          <TabsContent value="notifications" className="mt-6">
            <NotificationsTab />
          </TabsContent>
          <TabsContent value="reminders" className="mt-6">
            <RemindersTab />
          </TabsContent>
          <TabsContent value="security" className="mt-6">
            <SecurityTab />
          </TabsContent>
          <TabsContent value="calendar" className="mt-6">
            <CalendarTab />
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
